import type { ActiveBotEntity, BotRuntime } from "./bot-runtime";
import type { BotDefeatRuntime } from "./bot-defeat-runtime";
import type { GameFlow, GameFlowSnapshot } from "./game-flow";
import type { PlayerDefeatRuntime } from "./player-defeat-runtime";
import type { PlayerRuntime } from "./player-runtime";
import { gameplayConfig } from "../config/gameplay-config";
import { getPlayerGrowthProgress } from "../simulation/player-growth";
import {
  findBlockedBodyContacts,
  findTailClipContacts,
  getSnakeMouthPosition,
  resolveBodyBounce,
  type TailClipSnake,
} from "../simulation/tail-clipping";
import type { Vector2 } from "../simulation/player-movement";
import type { DefeatCombatResolution } from "../simulation/size-combat";

export interface TailClipRecord {
  readonly attackerGrowthAfter: number;
  readonly attackerGrowthBefore: number;
  readonly attackerId: string;
  readonly attackerLevelAfter: number;
  readonly attackerLevelBefore: number;
  readonly clippedBubbleCount: number;
  readonly growthTransferred: number;
  readonly occurredAtMs: number;
  readonly positions: readonly Vector2[];
  readonly removedBubbleIds: readonly string[];
  readonly revision: number;
  readonly victimGrowthAfter: number;
  readonly victimGrowthBefore: number;
  readonly victimId: string;
  readonly victimDefeated: boolean;
  readonly victimLevelAfter: number;
  readonly victimLevelBefore: number;
}

export interface TailClipRuntimeSnapshot {
  readonly activeContactCount: number;
  readonly bubblesClipped: number;
  readonly bubblesLost: number;
  readonly lastClip: TailClipRecord | null;
}

export type TailClipRuntimeListener = (
  snapshot: TailClipRuntimeSnapshot,
) => void;

type PlayerPort = Pick<
  PlayerRuntime,
  "applyBodyCollision" | "getSnapshot" | "setGrowth" | "subscribeFixedStep"
>;
type BotPort = Pick<
  BotRuntime,
  "applyBodyCollision" | "getSnapshot" | "setGrowth"
>;
type BotDefeatPort = Pick<BotDefeatRuntime, "consume">;
type PlayerDefeatPort = Pick<PlayerDefeatRuntime, "consume">;
const playerIdentifier = "player";

export class TailClipRuntime {
  private activePairs = new Set<string>();
  private bubblesClipped = 0;
  private bubblesLost = 0;
  private lastClip: TailClipRecord | null = null;
  private readonly listeners = new Set<TailClipRuntimeListener>();
  private previousMouths = new Map<string, Vector2>();
  private revision = 0;
  private scene: GameFlowSnapshot["scene"] = "home";

  public constructor(
    flow: GameFlow,
    private readonly playerRuntime: PlayerPort,
    private readonly botRuntime: BotPort,
    private readonly botDefeatRuntime: BotDefeatPort,
    private readonly playerDefeatRuntime: PlayerDefeatPort,
  ) {
    playerRuntime.subscribeFixedStep((_stepMilliseconds, simulationTimeMs) => {
      this.step(simulationTimeMs);
    });
    flow.subscribe((snapshot) => {
      this.applyFlow(snapshot);
    });
  }

  public getSnapshot(): TailClipRuntimeSnapshot {
    return {
      activeContactCount: this.activePairs.size,
      bubblesClipped: this.bubblesClipped,
      bubblesLost: this.bubblesLost,
      lastClip:
        this.lastClip === null
          ? null
          : {
              ...this.lastClip,
              positions: this.lastClip.positions.map((position) => ({
                ...position,
              })),
              removedBubbleIds: [...this.lastClip.removedBubbleIds],
            },
    };
  }

  public subscribe(listener: TailClipRuntimeListener): () => void {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private applyFlow(snapshot: GameFlowSnapshot): void {
    this.scene = snapshot.scene;
    if (snapshot.scene !== "gameplay") {
      this.reset();
      return;
    }
    this.reset();
  }

  private applyGrowth(
    identifier: string,
    growth: number,
    ripple: boolean,
  ): void {
    if (identifier === playerIdentifier) {
      this.playerRuntime.setGrowth(growth, ripple);
      return;
    }
    this.botRuntime.setGrowth(identifier, growth, ripple);
  }

  private applyBounce(
    identifier: string,
    position: Vector2,
    directionRadians: number,
  ): void {
    if (identifier === playerIdentifier) {
      this.playerRuntime.applyBodyCollision(position, directionRadians);
      return;
    }
    this.botRuntime.applyBodyCollision(identifier, position, directionRadians);
  }

  private createSnakes(): readonly TailClipSnake[] {
    const playerSnapshot = this.playerRuntime.getSnapshot();
    const player = playerSnapshot.player;
    const playerBody = playerSnapshot.body;
    const playerSnake: readonly TailClipSnake[] =
      player?.alive === true && playerBody != null
        ? [
            {
              body: playerBody.segments,
              directionRadians: player.directionRadians,
              growth: player.growth,
              id: playerIdentifier,
              level: player.level,
              position: player.position,
              radius: player.collision.radius,
            },
          ]
        : [];
    return [
      ...playerSnake,
      ...this.botRuntime
        .getSnapshot()
        .bots.map((bot) => this.createBotSnake(bot)),
    ];
  }

  private createBotSnake(bot: ActiveBotEntity): TailClipSnake {
    return {
      body: bot.body.segments,
      directionRadians: bot.directionRadians,
      growth: bot.growth,
      id: bot.id,
      level: bot.traits.level,
      position: bot.position,
      radius: bot.collision.radius,
    };
  }

  private reset(): void {
    this.activePairs.clear();
    this.bubblesClipped = 0;
    this.bubblesLost = 0;
    this.lastClip = null;
    this.previousMouths.clear();
    this.revision = 0;
    this.notify();
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getSnapshot());
    }
  }

  private step(simulationTimeMs: number): void {
    if (this.scene !== "gameplay") {
      return;
    }
    const snakes = this.createSnakes();
    const clipConfig = {
      baseSegmentCount: gameplayConfig.playerBody.baseSegmentCount,
      mouthForwardScale: gameplayConfig.tailClipping.mouthForwardScale,
      mouthRadiusScale: gameplayConfig.tailClipping.mouthRadiusScale,
    };
    const contacts = findTailClipContacts(snakes, this.previousMouths, {
      ...clipConfig,
    });
    const currentPairs = new Set(
      contacts.map(
        (contact) => `${contact.attackerId}\u0000${contact.victimId}`,
      ),
    );
    for (const contact of contacts) {
      this.resolveContact(contact, simulationTimeMs);
    }
    this.activePairs = currentPairs;
    const blockedContacts = findBlockedBodyContacts(
      this.createSnakes(),
      clipConfig,
    );
    for (const contact of blockedContacts) {
      const bounce = resolveBodyBounce(
        contact.attacker,
        contact,
        gameplayConfig.tailClipping.bodySeparationDistance,
      );
      this.applyBounce(
        contact.attackerId,
        bounce.position,
        bounce.directionRadians,
      );
    }
    this.previousMouths = new Map(
      this.createSnakes().map((snake) => [
        snake.id,
        getSnakeMouthPosition(snake, {
          ...clipConfig,
        }),
      ]),
    );
  }

  private resolveContact(
    contact: {
      readonly attackerId: string;
      readonly positions: readonly Vector2[];
      readonly victimId: string;
    },
    simulationTimeMs: number,
  ): void {
    const snakes = this.createSnakes();
    const attacker = snakes.find((snake) => snake.id === contact.attackerId);
    const victim = snakes.find((snake) => snake.id === contact.victimId);
    if (
      attacker === undefined ||
      victim === undefined ||
      attacker.level <= victim.level
    ) {
      return;
    }
    if (victim.level === 1) {
      this.resolveFinalContact(attacker, victim, simulationTimeMs);
      return;
    }
    const clippedBubbleCount = Math.min(
      contact.positions.length,
      victim.level - 1,
    );
    const victimLevelAfter = victim.level - clippedBubbleCount;
    const victimGrowthAfter =
      gameplayConfig.growth.thresholds[victimLevelAfter - 1];
    if (victimGrowthAfter === undefined) {
      throw new Error(
        `Growth threshold is missing for level ${String(victimLevelAfter)}`,
      );
    }
    const growthTransferred = victim.growth - victimGrowthAfter;
    const reducedVictimGrowthAfter = victim.growth - growthTransferred;
    const attackerGrowthAfter = attacker.growth + growthTransferred;
    const attackerLevelAfter = getPlayerGrowthProgress(
      attackerGrowthAfter,
      gameplayConfig.growth,
    ).level;
    this.applyGrowth(victim.id, reducedVictimGrowthAfter, false);
    this.applyGrowth(attacker.id, attackerGrowthAfter, true);
    this.bubblesClipped += clippedBubbleCount;
    this.bubblesLost += clippedBubbleCount;
    this.revision += 1;
    this.lastClip = {
      attackerGrowthAfter,
      attackerGrowthBefore: attacker.growth,
      attackerId: attacker.id,
      attackerLevelAfter,
      attackerLevelBefore: attacker.level,
      clippedBubbleCount,
      growthTransferred,
      occurredAtMs: simulationTimeMs,
      positions: contact.positions
        .slice(0, clippedBubbleCount)
        .map((position) => ({ ...position })),
      removedBubbleIds: Array.from(
        { length: clippedBubbleCount },
        (_value, index) =>
          `${victim.id}:growth-${String(victim.level - index)}`,
      ),
      revision: this.revision,
      victimGrowthAfter: reducedVictimGrowthAfter,
      victimGrowthBefore: victim.growth,
      victimId: victim.id,
      victimDefeated: false,
      victimLevelAfter,
      victimLevelBefore: victim.level,
    };
    this.notify();
  }

  private resolveFinalContact(
    attacker: TailClipSnake,
    victim: TailClipSnake,
    simulationTimeMs: number,
  ): void {
    const runId = this.playerRuntime.getSnapshot().runId;
    const defeat: DefeatCombatResolution = {
      contactId: `tail-clip-${String(this.revision + 1).padStart(4, "0")}`,
      defeatedId: victim.id,
      kind: "defeat",
      observedAtMs: simulationTimeMs,
      runId,
      winnerId: attacker.id,
    };
    const outcome =
      victim.id === playerIdentifier
        ? this.playerDefeatRuntime.consume(defeat)
        : this.botDefeatRuntime.consume(defeat);
    if (outcome.kind === "ignored") {
      return;
    }
    const growthTransferred = victim.growth;
    const attackerGrowthAfter = attacker.growth + growthTransferred;
    const attackerLevelAfter = getPlayerGrowthProgress(
      attackerGrowthAfter,
      gameplayConfig.growth,
    ).level;
    this.applyGrowth(attacker.id, attackerGrowthAfter, true);
    const clippedBubbleCount = victim.body.length;
    this.bubblesClipped += clippedBubbleCount;
    this.bubblesLost += clippedBubbleCount;
    this.revision += 1;
    this.lastClip = {
      attackerGrowthAfter,
      attackerGrowthBefore: attacker.growth,
      attackerId: attacker.id,
      attackerLevelAfter,
      attackerLevelBefore: attacker.level,
      clippedBubbleCount,
      growthTransferred,
      occurredAtMs: simulationTimeMs,
      positions: victim.body.map((segment) => ({ ...segment.position })),
      removedBubbleIds: victim.body.map(
        (_segment, index) => `${victim.id}:base-${String(index + 1)}`,
      ),
      revision: this.revision,
      victimGrowthAfter: 0,
      victimGrowthBefore: victim.growth,
      victimId: victim.id,
      victimDefeated: true,
      victimLevelAfter: 0,
      victimLevelBefore: victim.level,
    };
    this.notify();
  }
}
